import { AlertTriangle, CheckCircle2, XCircle, TrendingUp, DollarSign } from 'lucide-react';
import type { ComparisonSide, ComparisonDiagnosisResult } from '@/types/comparison';
import { normalizeRestaurantName } from '@/lib/normalize-names';

// ============================================
// Comparison Diagnosis — Auto-generated analysis
// ============================================

interface ComparisonDiagnosisProps {
  left: ComparisonSide;
  right: ComparisonSide;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// Diagnosis logic
function generateDiagnosis(left: ComparisonSide, right: ComparisonSide): ComparisonDiagnosisResult {
  const leftMetrics = left.adGroup.metrics;
  const rightMetrics = right.adGroup.metrics;

  // Calculate conversion rates
  const leftConvRate = leftMetrics.clicks > 0 ? (leftMetrics.conversions / leftMetrics.clicks) * 100 : 0;
  const rightConvRate = rightMetrics.clicks > 0 ? (rightMetrics.conversions / rightMetrics.clicks) * 100 : 0;

  const leftCtr = leftMetrics.ctr;
  const rightCtr = rightMetrics.ctr;

  const leftLpScore = left.landingPage?.scores.overall || 0;
  const rightLpScore = right.landingPage?.scores.overall || 0;

  const ctrDiff = Math.abs(leftCtr - rightCtr);
  const convDiff = Math.abs(leftConvRate - rightConvRate);
  const lpDiff = Math.abs(leftLpScore - rightLpScore);

  // Thresholds
  const CTR_SIMILAR = 1.0;      // 1% difference = similar
  const CONV_SIGNIFICANT = 2.0; // 2% difference = significant
  const LP_SIGNIFICANT = 20;    // 20 point difference = significant
  const GOOD_CTR = 1.5;
  const GOOD_CONV = 2.0;

  // Comparisons
  const ctrComparison = ctrDiff <= CTR_SIMILAR ? 'similar' : (leftCtr > rightCtr ? 'left_better' : 'right_better');
  const convRateComparison = convDiff <= CONV_SIGNIFICANT ? 'similar' : (leftConvRate > rightConvRate ? 'left_better' : 'right_better');
  const lpScoreComparison = lpDiff <= LP_SIGNIFICANT ? 'similar' : (leftLpScore > rightLpScore ? 'left_better' : 'right_better');

  // Determine verdict
  let verdict: ComparisonDiagnosisResult['verdict'] = 'inconclusive';
  let explanation = '';
  let potentialRevenueGain = 0;

  const leftIsHealthy = leftCtr >= GOOD_CTR && leftConvRate >= GOOD_CONV;
  const rightIsHealthy = rightCtr >= GOOD_CTR && rightConvRate >= GOOD_CONV;

  const leftHasLpProblem = leftCtr >= GOOD_CTR && leftConvRate < GOOD_CONV;
  const rightHasLpProblem = rightCtr >= GOOD_CTR && rightConvRate < GOOD_CONV;

  const leftHasAdProblem = leftCtr < GOOD_CTR;
  const rightHasAdProblem = rightCtr < GOOD_CTR;

  // Calculate average conversion value
  const leftAvgConvValue = leftMetrics.conversions > 0 ? leftMetrics.conversionValue / leftMetrics.conversions : 0;
  const rightAvgConvValue = rightMetrics.conversions > 0 ? rightMetrics.conversionValue / rightMetrics.conversions : 0;
  const avgConvValue = (leftAvgConvValue + rightAvgConvValue) / 2 || 500; // Default $500 if no data

  if (leftIsHealthy && rightIsHealthy) {
    verdict = 'both_healthy';
    explanation = `Both ad groups are performing well. ${normalizeRestaurantName(left.restaurant.name)} has ${formatPercent(leftCtr)} CTR with ${formatPercent(leftConvRate)} conversion, while ${normalizeRestaurantName(right.restaurant.name)} has ${formatPercent(rightCtr)} CTR with ${formatPercent(rightConvRate)} conversion.`;
  } else if (leftHasAdProblem && rightHasAdProblem) {
    verdict = 'ad_issue';
    explanation = `Both restaurants have ad performance issues with low CTR (${formatPercent(leftCtr)} and ${formatPercent(rightCtr)}). Consider revising ad copy and keywords.`;
  } else if (leftHasLpProblem && !rightHasLpProblem && ctrComparison === 'similar') {
    // Left has LP problem, right is healthy, CTRs are similar
    verdict = 'landing_page_issue';
    const potentialConversions = (leftMetrics.clicks * rightConvRate / 100) - leftMetrics.conversions;
    potentialRevenueGain = Math.max(0, potentialConversions * avgConvValue);
    explanation = `Both restaurants have similar CTR (~${formatPercent((leftCtr + rightCtr) / 2)}), proving ads work equally well. However, ${normalizeRestaurantName(left.restaurant.name)} converts at only ${formatPercent(leftConvRate)} vs ${normalizeRestaurantName(right.restaurant.name)}'s ${formatPercent(rightConvRate)}. The landing page score difference (${leftLpScore} vs ${rightLpScore}) explains the gap.`;
  } else if (rightHasLpProblem && !leftHasLpProblem && ctrComparison === 'similar') {
    // Right has LP problem, left is healthy, CTRs are similar
    verdict = 'landing_page_issue';
    const potentialConversions = (rightMetrics.clicks * leftConvRate / 100) - rightMetrics.conversions;
    potentialRevenueGain = Math.max(0, potentialConversions * avgConvValue);
    explanation = `Both restaurants have similar CTR (~${formatPercent((leftCtr + rightCtr) / 2)}), proving ads work equally well. However, ${normalizeRestaurantName(right.restaurant.name)} converts at only ${formatPercent(rightConvRate)} vs ${normalizeRestaurantName(left.restaurant.name)}'s ${formatPercent(leftConvRate)}. The landing page score difference (${rightLpScore} vs ${leftLpScore}) explains the gap.`;
  } else if ((leftHasLpProblem || rightHasLpProblem) && (leftHasAdProblem || rightHasAdProblem)) {
    verdict = 'both_issues';
    explanation = `Mixed performance: One restaurant has ad issues (low CTR), while another has landing page issues (good CTR but low conversion). Both need optimization.`;
  } else {
    // Inconclusive - provide descriptive explanation
    explanation = `${normalizeRestaurantName(left.restaurant.name)}: ${formatPercent(leftCtr)} CTR, ${formatPercent(leftConvRate)} conversion, LP score ${leftLpScore}. ${normalizeRestaurantName(right.restaurant.name)}: ${formatPercent(rightCtr)} CTR, ${formatPercent(rightConvRate)} conversion, LP score ${rightLpScore}.`;
  }

  return {
    ctrComparison,
    convRateComparison,
    lpScoreComparison,
    verdict,
    potentialRevenueGain,
    explanation,
    details: {
      leftCtr,
      rightCtr,
      leftConvRate,
      rightConvRate,
      leftLpScore,
      rightLpScore,
    },
  };
}

// Verdict styling
const verdictStyles = {
  landing_page_issue: {
    icon: AlertTriangle,
    title: 'Landing Page Issue Detected',
    bg: 'bg-[var(--ed-amber-bg)]',
    border: 'border-[rgba(224,168,82,0.15)]',
    iconColor: 'text-[var(--ed-amber)]',
    titleColor: 'text-[var(--ed-amber)]',
  },
  ad_issue: {
    icon: XCircle,
    title: 'Ad Performance Issue',
    bg: 'bg-[var(--ed-red-bg)]',
    border: 'border-[rgba(224,90,82,0.15)]',
    iconColor: 'text-[var(--ed-red)]',
    titleColor: 'text-[var(--ed-red)]',
  },
  both_issues: {
    icon: AlertTriangle,
    title: 'Multiple Issues Found',
    bg: 'bg-[var(--ed-amber-bg)]',
    border: 'border-[rgba(224,168,82,0.15)]',
    iconColor: 'text-[var(--ed-amber)]',
    titleColor: 'text-[var(--ed-amber)]',
  },
  both_healthy: {
    icon: CheckCircle2,
    title: 'Both Performing Well',
    bg: 'bg-[var(--ed-green-bg)]',
    border: 'border-[rgba(74,186,122,0.15)]',
    iconColor: 'text-[var(--ed-green)]',
    titleColor: 'text-[var(--ed-green)]',
  },
  inconclusive: {
    icon: TrendingUp,
    title: 'Comparison Summary',
    bg: 'bg-[var(--ed-bg-2)]',
    border: 'border-[var(--ed-rule-heavy)]',
    iconColor: 'text-[var(--ed-t2)]',
    titleColor: 'text-[var(--ed-t1)]',
  },
};

export function ComparisonDiagnosis({ left, right }: ComparisonDiagnosisProps) {
  const diagnosis = generateDiagnosis(left, right);
  const style = verdictStyles[diagnosis.verdict];
  const Icon = style.icon;

  return (
    <div className={`rounded-xl border ${style.border} ${style.bg} p-5`}>
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg bg-[var(--ed-bg-3)] ${style.iconColor}`}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={`text-lg font-medium ${style.titleColor}`}>{style.title}</h3>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {diagnosis.explanation}
          </p>

          {/* Comparison metrics row */}
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">CTR</p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-sm tabular-nums">{formatPercent(diagnosis.details.leftCtr)}</span>
                <span className="text-xs text-muted-foreground/50">vs</span>
                <span className="text-sm tabular-nums">{formatPercent(diagnosis.details.rightCtr)}</span>
              </div>
              <span className={`text-[10px] ${
                diagnosis.ctrComparison === 'similar' ? 'text-[var(--ed-t2)]' :
                diagnosis.ctrComparison === 'left_better' ? 'text-[var(--ed-green)]' : 'text-[var(--ed-green)]'
              }`}>
                {diagnosis.ctrComparison === 'similar' ? 'Similar' :
                 diagnosis.ctrComparison === 'left_better' ? `${normalizeRestaurantName(left.restaurant.name)} better` : `${normalizeRestaurantName(right.restaurant.name)} better`}
              </span>
            </div>

            <div className="text-center">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Conv Rate</p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-sm tabular-nums">{formatPercent(diagnosis.details.leftConvRate)}</span>
                <span className="text-xs text-muted-foreground/50">vs</span>
                <span className="text-sm tabular-nums">{formatPercent(diagnosis.details.rightConvRate)}</span>
              </div>
              <span className={`text-[10px] ${
                diagnosis.convRateComparison === 'similar' ? 'text-[var(--ed-t2)]' :
                diagnosis.convRateComparison === 'left_better' ? 'text-[var(--ed-green)]' : 'text-[var(--ed-green)]'
              }`}>
                {diagnosis.convRateComparison === 'similar' ? 'Similar' :
                 diagnosis.convRateComparison === 'left_better' ? `${normalizeRestaurantName(left.restaurant.name)} better` : `${normalizeRestaurantName(right.restaurant.name)} better`}
              </span>
            </div>

            <div className="text-center">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">LP Score</p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-sm tabular-nums">{diagnosis.details.leftLpScore || '-'}</span>
                <span className="text-xs text-muted-foreground/50">vs</span>
                <span className="text-sm tabular-nums">{diagnosis.details.rightLpScore || '-'}</span>
              </div>
              <span className={`text-[10px] ${
                diagnosis.lpScoreComparison === 'similar' ? 'text-[var(--ed-t2)]' :
                diagnosis.lpScoreComparison === 'left_better' ? 'text-[var(--ed-green)]' : 'text-[var(--ed-green)]'
              }`}>
                {diagnosis.lpScoreComparison === 'similar' ? 'Similar' :
                 diagnosis.lpScoreComparison === 'left_better' ? `${normalizeRestaurantName(left.restaurant.name)} better` : `${normalizeRestaurantName(right.restaurant.name)} better`}
              </span>
            </div>
          </div>

          {/* Potential revenue gain */}
          {diagnosis.potentialRevenueGain > 0 && (
            <div className="mt-4 pt-4 border-t border-border/20 flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-[var(--ed-green)]" />
              <div>
                <p className="text-sm font-medium text-[var(--ed-green)]">
                  Potential Revenue Gain: {formatCurrency(diagnosis.potentialRevenueGain)}/month
                </p>
                <p className="text-xs text-muted-foreground">
                  If landing page issues are fixed to match the better performer
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
